import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { FieldLabel } from "../shared/FieldHint";
import { formatET } from "../../lib/dateUtils";
import { api, formatApiErrorDetail } from "../../lib/api";

export function RepAgreementTab() {
  const [agreement, setAgreement] = useState(null);
  const [form, setForm] = useState({ full_legal_name: "", email: "", phone: "", agree: false });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [justSigned, setJustSigned] = useState(false);

  useEffect(() => {
    api.get("/rep/agreement")
      .then(({ data }) => setAgreement(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agree) { toast.error("Check the agree box to sign"); return; }
    if (!form.full_legal_name.trim()) { toast.error("Full legal name is required"); return; }
    setSubmitting(true);
    try {
      await api.post("/rep/agreement/sign", form);
      toast.success("Agreement submitted successfully");
      setJustSigned(true);
      const { data } = await api.get("/rep/agreement");
      setAgreement(data);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !agreement) return null;

  return (
    <div data-testid="rep-agreement-tab" className="max-w-3xl">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-serif text-xl text-igc-cream tracking-tight" title="Your official partnership contract with IGC. It has to be signed before some payouts and features fully unlock.">IGC Rep Partner Agreement ({agreement.agreement_code})</h2>
        {agreement.signed ? (
          <span className="text-[10px] px-3 py-1.5 uppercase tracking-wide bg-igc-success/10 text-igc-success border border-igc-success/30" data-testid="rep-agreement-signed-badge" title="This agreement is fully signed and on file with IGC — no further action needed.">
            Signed — {formatET(agreement.signed_at, "MMM d, yyyy")}
          </span>
        ) : (
          <span className="text-[10px] px-3 py-1.5 uppercase tracking-wide bg-igc-gold/15 text-igc-goldText border border-igc-gold/40" data-testid="rep-agreement-action-required-badge" title="You have not signed this agreement yet. Scroll down, read it, fill in the form, check the box, and click 'I Accept & Sign' to complete it.">
            Action Required — Sign Now
          </span>
        )}
      </div>

      {justSigned && agreement.signed && (
        <div className="bg-igc-success/10 border border-igc-success/30 p-4 mb-6 text-igc-success text-sm" data-testid="rep-agreement-confirmation-banner">
          Your agreement was submitted successfully and is on file with IGC. A confirmation email will follow shortly.
        </div>
      )}

      <div className="bg-igc-surface border border-igc-gold/15 p-5 mb-6 whitespace-pre-wrap text-igc-cream text-sm leading-relaxed" data-testid="rep-agreement-text" title="The full legal text of your partnership agreement with IGC — please read it in full before signing below.">
        {agreement.agreement_text}
      </div>

      {agreement.signed ? (
        <p className="text-igc-muted text-sm" data-testid="rep-agreement-already-signed-note">Signed by {agreement.full_legal_name} — thanks, no further action needed.</p>
      ) : (
        <form onSubmit={submit} className="space-y-3 bg-igc-surface border border-igc-gold/15 p-5" data-testid="rep-agreement-sign-form">
          <Input data-testid="rep-agreement-legal-name" placeholder="Full legal name" title="Type your full legal name exactly as it should appear on this contract — this becomes your digital signature." value={form.full_legal_name} onChange={(e) => setForm({ ...form, full_legal_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="rep-agreement-email" placeholder="Email" title="Your email address, for IGC's records on this agreement." value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
            <Input data-testid="rep-agreement-phone" placeholder="Phone" title="Your phone number, for IGC's records on this agreement." value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
          </div>
          <Input data-testid="rep-agreement-date" disabled value={new Date().toLocaleDateString()} title="Today's date — filled in automatically, this cannot be changed." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-muted" />
          <label className="flex items-center gap-2 text-igc-cream text-sm">
            <Checkbox data-testid="rep-agreement-agree-checkbox" checked={form.agree} onCheckedChange={(v) => setForm({ ...form, agree: !!v })} title="You must check this box to confirm you've read and agree to the contract text above before you can sign." />
            I have read and agree to the terms of this Agreement
            <FieldLabel hint="Checking this and submitting counts as your digital signature on the agreement above." testId="rep-agreement-agree-hint" />
          </label>
          <Button data-testid="rep-agreement-sign-button" type="submit" disabled={submitting || !form.agree} title="Submits your name, contact info, and checked agreement as your official digital signature on this contract — this cannot be undone." className="bg-igc-gold hover:bg-igc-goldHover text-black disabled:opacity-40 disabled:cursor-not-allowed">I Accept & Sign</Button>
        </form>
      )}
    </div>
  );
}
